import React, { useState, useEffect, useRef } from 'react';
import { Bell, BellOff, Settings, Moon, Sun, Upload, ChevronDown, LogOut, FileText, User } from 'lucide-react';
import { formatTimestamp } from '../../utils/formatters';
import { VinCentrixLogo } from '../Logo/VinCentrixLogo';

/**
 * Header Component
 * Top header section with logo, actions, and user controls
 * @param {Object} props - Component props
 * @param {Object} props.user - Current user object
 * @param {Array} props.notifications - Array of notification objects
 * @param {Function} props.onClearNotifications - Function to clear all notifications
 * @param {boolean} props.isDarkMode - Whether dark mode is enabled
 * @param {Function} props.onToggleTheme - Function to toggle theme
 * @param {Function} props.onOpenSettings - Function to open settings modal
 * @param {Function} props.onLogout - Function to handle logout
 * @param {Function} props.onImportCSV - Function to open CSV import modal
 * @param {Function} props.onOpenAppraisalSettings - Function to open appraisal settings
 * @param {Object} props.theme - Theme object with styling properties
 * @param {Object} props.colors - Color palette object
 * @param {boolean} props.notificationsEnabled - Whether notifications are enabled
 */
export const Header = ({
  user,
  notifications = [],
  onClearNotifications,
  isDarkMode,
  onToggleTheme,
  onOpenSettings,
  onLogout,
  onImportCSV,
  onOpenAppraisalSettings,
  theme,
  colors,
  notificationsEnabled = true
}) => {
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const getStoreName = (user) => {
    if (!user) return 'VinCentrix';
    // Check if it's a dealer object (has 'name' property) or user object (has 'dealershipName')
    return user.name || user.dealershipName || 'VinCentrix';
  };

  // Get user display name
  const getUserDisplayName = (user) => {
    if (!user) return 'User';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.name || user.username || user.email || 'User';
  };

  // Get user initials for avatar
  const getUserInitials = (user) => {
    if (!user) return 'U';
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.name) {
      const parts = user.name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return user.name[0].toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);

  if (!user) return null;

  return (
    <div style={{
      background: isDarkMode
        ? 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 20%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0.05) 80%, rgba(255, 255, 255, 0.1) 100%)'
        : 'linear-gradient(145deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.6) 100%)',
      backdropFilter: 'blur(40px) saturate(150%)',
      WebkitBackdropFilter: 'blur(40px) saturate(150%)',
      borderBottom: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.15)' : 'rgba(59, 130, 246, 0.15)'}`,
      padding: '0 32px',
      height: '80px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'sticky',
      top: 0,
      zIndex: 100,
      boxShadow: isDarkMode
        ? '0 4px 30px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.2), 0 0 60px rgba(59, 130, 246, 0.08)'
        : '0 10px 40px rgba(0, 0, 0, 0.08)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
        <div style={{ marginRight: '8px' }}>
          <VinCentrixLogo size={44} showText={true} />
        </div>
        <div>
          <h1 style={{ 
            fontSize: '22px', 
            fontWeight: '600',
            color: theme.text,
            marginBottom: '4px',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
          }}>
            {getStoreName(user)}
          </h1>
          <p style={{ 
            fontSize: '13px', 
            color: theme.textSecondary,
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
          }}>
            Powered by VinCentrix AI
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {/* Notification Bell - Glass Icon Button */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            style={{
              width: '48px',
              height: '48px',
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '50%',
              padding: 0,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              position: 'relative',
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(59, 130, 246, 0.15)';
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.3)';
              e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255, 255, 255, 0.1)';
            }}
          >
            {notificationsEnabled ? (
              <Bell size={22} color="rgba(255, 255, 255, 0.7)" />
            ) : (
              <BellOff size={22} color="rgba(255, 255, 255, 0.5)" />
            )}
            {notifications.length > 0 && (
              <span style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                width: '10px',
                height: '10px',
                background: '#ef4444',
                borderRadius: '50%',
                boxShadow: '0 0 10px #ef4444, 0 0 20px rgba(239, 68, 68, 0.5)',
                animation: 'statusPulse 2s ease-in-out infinite'
              }} />
            )}
          </button>

          {showNotifications && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              width: '360px',
              maxHeight: '400px',
              overflowY: 'auto',
              background: isDarkMode
                ? 'linear-gradient(145deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 50%, rgba(255,255,255,0.05) 100%)'
                : 'linear-gradient(145deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.6) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              border: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(59, 130, 246, 0.15)'}`,
              borderRadius: '16px',
              boxShadow: isDarkMode
                ? '0 0 30px rgba(59, 130, 246, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                : '0 10px 40px rgba(0, 0, 0, 0.08)',
              zIndex: 1000
            }}>
              <div style={{
                padding: '16px',
                borderBottom: '1px solid rgba(59, 130, 246, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600' }}>Notifications</h3>
                {notifications.length > 0 && (
                  <button
                    onClick={() => {
                      onClearNotifications();
                      setShowNotifications(false);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: theme.textSecondary,
                      cursor: 'pointer',
                      fontSize: '12px',
                      padding: '4px 8px'
                    }}
                  >
                    Clear all
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', color: theme.textSecondary }}>
                  <Bell size={32} style={{ opacity: 0.3, marginBottom: '8px' }} />
                  <p>No notifications</p>
                </div>
              ) : (
                notifications.map(notif => (
                  <div
                    key={notif.id}
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid rgba(59, 130, 246, 0.1)',
                      cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = 'rgba(59, 130, 246, 0.05)'}
                    onMouseOut={(e) => e.currentTarget.style.background = 'transparent'}
                  >
                    <p style={{ fontSize: '13px', marginBottom: '4px' }}>{notif.message}</p>
                    <p style={{ fontSize: '11px', color: theme.textSecondary }}>
                      {formatTimestamp(notif.timestamp)}
                    </p>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* User Profile Dropdown */}
        <div style={{ position: 'relative' }} ref={dropdownRef}>
        <button
            onClick={() => setShowUserDropdown(!showUserDropdown)}
          style={{
            display: 'flex',
            alignItems: 'center',
              gap: '12px',
              padding: '10px 16px',
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '14px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
              e.currentTarget.style.borderColor = 'rgba(59, 130, 246, 0.2)';
              e.currentTarget.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.boxShadow = 'inset 0 1px 0 rgba(255, 255, 255, 0.1)';
            }}
          >
            {/* Avatar Circle */}
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              fontSize: '15px',
              fontWeight: '600',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
              boxShadow: '0 0 20px rgba(59, 130, 246, 0.4)'
            }}>
              {getUserInitials(user)}
            </div>
            
            {/* User Name */}
            <span style={{
              fontSize: '15px',
              fontWeight: '600',
              color: '#fff',
              fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
            }}>
              {getUserDisplayName(user)}
            </span>
            
            {/* Chevron */}
            <ChevronDown 
              size={18} 
              color="rgba(255, 255, 255, 0.6)"
              style={{
                transition: 'transform 0.3s ease',
                transform: showUserDropdown ? 'rotate(180deg)' : 'rotate(0deg)'
              }}
            />
        </button>

          {/* Dropdown Menu */}
          {showUserDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              width: '260px',
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 20%, rgba(255, 255, 255, 0.02) 50%, rgba(255, 255, 255, 0.05) 80%, rgba(255, 255, 255, 0.1) 100%)',
              backdropFilter: 'blur(40px) saturate(150%)',
              WebkitBackdropFilter: 'blur(40px) saturate(150%)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
              overflow: 'hidden',
              zIndex: 1000,
              animation: 'fadeInUp 0.3s ease-out'
            }}>
              {/* Top highlight */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: '10%',
                right: '10%',
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)'
              }} />
              
              {/* User Info Header (non-clickable) */}
              <div style={{
                padding: '16px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
                position: 'relative',
                zIndex: 1
              }}>
                <div style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#fff',
                  marginBottom: '4px',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
                }}>
                  {getUserDisplayName(user)}
                </div>
                {user.email && (
                  <div style={{
                    fontSize: '13px',
                    color: 'rgba(255, 255, 255, 0.6)',
                    marginBottom: '8px',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
                  }}>
                    {user.email}
                  </div>
                )}
                <div style={{
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.4)',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
                }}>
                  {getStoreName(user)}
                </div>
              </div>

              {/* Divider */}
              <div style={{
                height: '1px',
                background: 'rgba(255, 255, 255, 0.1)',
                margin: '8px 0'
              }} />

              {/* Menu Items */}
              <div style={{ position: 'relative', zIndex: 1 }}>
                {onOpenAppraisalSettings && (
                  <div
                    onClick={() => {
                      onOpenAppraisalSettings();
                      setShowUserDropdown(false);
                    }}
          style={{
                      padding: '12px 20px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
                      gap: '12px',
                      transition: 'all 0.2s ease',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <Settings size={18} color="rgba(255, 255, 255, 0.7)" />
                    <span style={{
                      fontSize: '15px',
                      color: '#fff',
                      fontWeight: '500'
                    }}>
                      Appraisal Settings
                    </span>
        </div>
                )}

                {onImportCSV && (
                  <div
                    onClick={() => {
                      onImportCSV();
                      setShowUserDropdown(false);
                    }}
          style={{
                      padding: '12px 20px',
            cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      transition: 'all 0.2s ease',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                    }}
                  >
                    <Upload size={18} color="rgba(255, 255, 255, 0.7)" />
                    <span style={{
                      fontSize: '15px',
                      color: '#fff',
                      fontWeight: '500'
                    }}>
                      Import CSV
                    </span>
                  </div>
                )}

                <div
                  onClick={() => {
                    onOpenSettings();
                    setShowUserDropdown(false);
                  }}
            style={{
                    padding: '12px 20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.2s ease',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <Settings size={18} color="rgba(255, 255, 255, 0.7)" />
                  <span style={{
                    fontSize: '15px',
                    color: '#fff',
                    fontWeight: '500'
                  }}>
                    Settings
                  </span>
                </div>

                {/* Divider */}
                <div style={{
                  height: '1px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  margin: '8px 0'
                }} />

                {/* Log Out */}
                <div
                  onClick={() => {
                    onLogout();
                    setShowUserDropdown(false);
                  }}
            style={{
                    padding: '12px 20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
                    gap: '12px',
                    transition: 'all 0.2s ease',
                    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <LogOut size={18} color="rgba(239, 68, 68, 0.8)" />
                  <span style={{
                    fontSize: '15px',
                    color: 'rgba(239, 68, 68, 0.9)',
                    fontWeight: '500'
                  }}>
                    Log Out
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

